import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { auth, db, doc, getDoc } from '../lib/firebase';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  userProfile: UserProfile | null;
  error: string | null;
  isAdmin: boolean;
}

interface UserProfile {
  email: string;
  agencyId: string;
  userRole: 'admin' | 'user';
  createdAt: Date;
  permissions?: {
    collaborations: boolean;
    todo: boolean;
    clients: boolean;
    influencers: boolean;
    casting: boolean;
    accounting: boolean;
    resources: boolean;
    assistant: boolean;
    dashboard: boolean;
  };
}

const AuthContext = createContext<AuthContextType>({ 
  currentUser: null, 
  loading: true,
  userProfile: null,
  error: null,
  isAdmin: false
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fonction pour charger le profil utilisateur
  const loadUserProfile = async (user: User): Promise<void> => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data() as UserProfile;
        setUserProfile(userData);
        
        // Super admin est toujours admin
        if (user.email === 'hosni@swipe-upp.com') {
          setIsAdmin(true);
          return;
        }
        
        // Vérifier si l'utilisateur est admin ou propriétaire de l'agence
        const isUserAdmin = userData.userRole === 'admin';
        
        // Si l'utilisateur a une agence, vérifier s'il en est le propriétaire
        let isAgencyOwner = false;
        if (userData.agencyId) {
          const agencyDoc = await getDoc(doc(db, 'agencies', userData.agencyId));
          if (agencyDoc.exists()) {
            isAgencyOwner = agencyDoc.data().ownerId === user.uid;
          }
        }
        
        // L'utilisateur est considéré admin s'il est marqué comme admin ou s'il est propriétaire de l'agence
        setIsAdmin(isUserAdmin || isAgencyOwner);
      } else {
        setUserProfile(null);
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setUserProfile(null);
      setIsAdmin(false);
    }
  };

  // Effet pour gérer l'authentification
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setLoading(true);
      try {
        setCurrentUser(user);
        setError(null);
        
        if (user) {
          await loadUserProfile(user);
        } else {
          setUserProfile(null);
          setIsAdmin(false);
        }
      } catch (error) {
        console.error('Auth state change error:', error);
        setUserProfile(null);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Effet pour recharger le profil périodiquement
  useEffect(() => {
    if (!currentUser) return;
    
    // Recharger le profil toutes les 10 secondes pour s'assurer qu'il est à jour
    const interval = setInterval(() => {
      if (currentUser) {
        loadUserProfile(currentUser);
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [currentUser]);

  return (
    <AuthContext.Provider value={{ currentUser, loading, userProfile, error, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}